const SENSITIVE_KEY_PATTERN = /token|authorization|password|secret|cookie|jwt|magiclink|emailbody|body|payload|code/i;
const MAX_OBJECT_DEPTH = 4;
const MAX_ARRAY_ITEMS = 20;
const MAX_OBJECT_KEYS = 40;

export const sanitizeString = (value: unknown, maxLength: number): string => {
  let text: string;
  try {
    text = typeof value === 'string' ? value : String(value ?? '');
  } catch {
    text = '[Unserializable]';
  }
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
};

export const sanitizeUrl = (value: unknown, maxLength = 500): string | undefined => {
  if (typeof value !== 'string' || value.trim().length === 0) return undefined;

  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const parsed = new URL(value, base);
    const sanitized = parsed.origin === base
      ? parsed.pathname
      : `${parsed.origin}${parsed.pathname}`;
    return sanitizeString(sanitized, maxLength);
  } catch {
    const [withoutHash] = value.split('#');
    const [withoutQuery] = withoutHash.split('?');
    return sanitizeString(withoutQuery, maxLength);
  }
};

export const sanitizeMetadata = (
  value: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined => {
  if (!value) return undefined;

  const sanitized = sanitizeValue(value, 0, new WeakSet<object>());
  return sanitized && typeof sanitized === 'object' && !Array.isArray(sanitized)
    ? sanitized as Record<string, unknown>
    : undefined;
};

const sanitizeValue = (
  value: unknown,
  depth: number,
  seen: WeakSet<object>,
): unknown => {
  if (value === null || value === undefined) return value;

  if (typeof value === 'string') return sanitizeString(value, 500);
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (typeof value === 'bigint') return value.toString();
  if (typeof value === 'function' || typeof value === 'symbol') return `[${typeof value}]`;
  if (value instanceof Date) {
    try {
      return value.toISOString();
    } catch {
      return '[Invalid Date]';
    }
  }
  if (value instanceof Error) {
    return {
      name: sanitizeString(value.name, 200),
      message: sanitizeString(value.message, 500),
    };
  }

  if (typeof value !== 'object') return sanitizeString(value, 500);

  if (seen.has(value)) return '[Circular]';
  if (depth >= MAX_OBJECT_DEPTH) return '[Truncated]';

  seen.add(value);

  if (Array.isArray(value)) {
    try {
      return value
        .slice(0, MAX_ARRAY_ITEMS)
        .map((item) => sanitizeValue(item, depth + 1, seen));
    } catch {
      return '[Unserializable Array]';
    }
  }

  const output: Record<string, unknown> = {};
  let entries: [string, unknown][];
  try {
    entries = Object.entries(value).slice(0, MAX_OBJECT_KEYS);
  } catch {
    return '[Unserializable Object]';
  }

  for (const [key, item] of entries) {
    if (SENSITIVE_KEY_PATTERN.test(key)) {
      output[key] = '[Redacted]';
      continue;
    }
    output[key] = sanitizeValue(item, depth + 1, seen);
  }

  return output;
};
